import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/dashboard/indicators';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetMyAccountDashboardIndicatorsInput = paths[url]['get']['parameters']['query'];

export interface GetMyAccountDashboardIndicatorsSuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

export const useGetMyAccountDashboardIndicators = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setMyAccountDashboardIndicators, setIsLoadingMyAccountDashboardIndicators } = useData();

    const request = async (props: GetMyAccountDashboardIndicatorsInput = {}) => {
        setIsLoadingMyAccountDashboardIndicators(true);

        const propsString = JSON.stringify({ ...props });

        try {
            const inputParameters = new URLSearchParams({ ...getCommonParameters(), ...JSON.parse(propsString) }).toString();

            const response = await fetch(`${API_URL}${ENDPOINT_URL}?${inputParameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (innerError: unknown) {
                    const fallback = innerError as ApiError;
                    error.message = fallback?.message ?? '';
                }

                error.statusCode = response.status;

                throw error;
            }

            const data: GetMyAccountDashboardIndicatorsSuccessResponse = await response.json();

            setMyAccountDashboardIndicators(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
        } finally {
            setIsLoadingMyAccountDashboardIndicators(false);
        }
    };

    return { request };
};
